import React from 'react';
import {Alert} from 'reactstrap';
import {parseGoogleWorkspaceLink} from '../../lib/googleWorkspaceLinks.ts';

interface GoogleEmbedHintProps {
    url: string | null | undefined;
    /** Called with the recommended embed link when the author accepts it. */
    onUseEmbedUrl?: (url: string) => void;
}

/*
 * Authoring-time guidance for Google Docs / Slides / Sheets / Forms / Drive
 * links. Shown under the URL field. The owner of a file always sees it
 * because they are signed in to Google; this says what everyone else needs.
 */
const GoogleEmbedHint: React.FC<GoogleEmbedHintProps> = ({
    url,
    onUseEmbedUrl
}) => {
    const link = parseGoogleWorkspaceLink(url);

    if (!link) {
        return null;
    }

    const sharingReminder = (
        <>
            In Google, open <strong>Share</strong> and set{' '}
            <strong>General access</strong> to{' '}
            <strong>Anyone with the link</strong>. You can always see your own
            file, so this is the only way to know others will too.
        </>
    );

    if (link.isEmbedLink) {
        return (
            <Alert color="info" className="small mt-2 mb-2">
                {link.label} embed link. {sharingReminder}
            </Alert>
        );
    }

    return (
        <Alert color="warning" className="small mt-2 mb-2">
            <p className="mb-2">
                This is a {link.label} <strong>editing</strong> link. Google
                only shows it to people signed in with access to the file, and
                usually refuses to show editing links inside another page, so
                viewers of this project will see an error instead of your
                content.
            </p>
            {link.embedUrl ? (
                <p className="mb-2">
                    Use the embed link instead:{' '}
                    <code className="text-break">{link.embedUrl}</code>
                    {onUseEmbedUrl && (
                        <>
                            {' '}
                            <button
                                type="button"
                                className="btn btn-sm btn-outline-primary ms-1"
                                onClick={() => onUseEmbedUrl(link.embedUrl!)}
                            >
                                Use embed link
                            </button>
                        </>
                    )}
                </p>
            ) : (
                <p className="mb-2">
                    Use the link Google gives you under <strong>Send</strong> →{' '}
                    <strong>&lt;&gt;</strong> (embed) instead of the editing
                    link.
                </p>
            )}
            <p className="mb-0">{sharingReminder}</p>
        </Alert>
    );
};

export default GoogleEmbedHint;
